/* New Design System Theme */

// Primary colors
const primary = "FF5879";
const primaryForeground = "#ffffff";

// Background colors (dark mode)
const background = "#1f1f1f";
const foreground = "#f0f0f0";
const card = "#2a2a2a";
const cardForeground = "#f0f0f0";
const popover = "#2a2a2a";
const popoverForeground = "#f0f0f0";

// Secondary/Muted colors
const secondary = "#3a3a3a";
const secondaryForeground = "#e0e0e0";
const muted = "#3a3a3a";
const mutedForeground = "#a0a0a0";

// Accent colors
const accent = "#35354f";
const accentForeground = "FF5879";

// State colors
const destructive = "#ef4444";
const destructiveForeground = "#ffffff";
const success = "#22c55e";
const successForeground = "#ffffff";
const warning = "#f59e0b";
const warningForeground = "#ffffff";

// Border/Input colors
const border = "#4a4a4a";
const input = "#3a3a3a";
const ring = "FF5879";

// AI color
const aiColor = "#AB7AE0";

// Connector colors (preserved for mod system)
const connectors = {
  mediaAsset: "#F3956A",
  text: "#65A9F3",
  color: "#F3EA62",
  advanced: "#58D1C9",
  interactive: "#AB7AE0",
  vidAndAudio: "#9DFF03",
  input: "#F37FB7",
  utils: "#8FD460",
};

const theme = {
  // New semantic colors
  primary,
  primaryForeground,
  background,
  foreground,
  card,
  cardForeground,
  popover,
  popoverForeground,
  secondary,
  secondaryForeground,
  muted,
  mutedForeground,
  accent,
  accentForeground,
  destructive,
  destructiveForeground,
  success,
  successForeground,
  warning,
  warningForeground,
  border,
  input,
  ring,
  aiColor,
  connectors,

  // Legacy aliases for backward compatibility
  _primary: {
    light: primary,
    dark: primary,
    highlight: primary,
    accent: accentForeground,
    hover: primary,
    disabled: primary,
  },
  error: destructive,
  link: accentForeground,
  blings_gray: mutedForeground,
  blings_gray_new: border,
  blings_purple: popover,
  blings_blue: "#5cc2e1",
  blings_success: success,
  blings_error: destructive,
  blings_that_gray: background,
  blings_teal: background,
  blings_teal_80: muted,
  blings_teal_60: mutedForeground,
  ai_color: aiColor,
};

// Legacy antdTheme object (not used by new Ant Design v5, kept for reference)
const antdTheme = {
  "@primary-color": theme.primary,
  "@primary-color-hover": theme.primary,
  "@info-color": theme.primary,
  "@processing-color": theme.primary,
  "@link-color": theme.accent,
  "@success-color": theme.success,
  "@warning-color": theme.warning,
  "@error-color": theme.destructive,
  "@layout-header-background": theme.background,
  "@font-size-base": "14px",
  "@heading-color": theme.foreground,
  "@text-color": theme.foreground,
  "@text-color-secondary": theme.mutedForeground,
  "@disabled-color": "rgba(255, 255, 255, .25)",
  "@border-radius-base": "8px",
  "@border-color-base": theme.border,
  "@box-shadow-base": "0 2px 8px rgba(0, 0, 0, 0.15)",
};

// CSS root variables export (minimal - main theme comes from theme.css)
const cssRootVars = `
:root {
  /* Amplify variables for auth components */
  --amplify-primary-color: ${theme.primary};
  --amplify-primary-tint: ${theme.primary};
  --amplify-primary-shade: ${theme.primary};

  --amazonOrange: ${theme.primary};
  --lightAmazonOrange: ${theme.primary};
  --darkAmazonOrange: ${theme.primary};
  --squidInk: ${theme.background};
  --lightSquidInk: ${theme.card};
  --deepSquidInk: ${theme.background};
  --grey: ${theme.mutedForeground};
  --lightGrey: ${theme.border};
  --silver: ${theme.border};
  --darkBlue: ${theme.card};
  --red: ${theme.destructive};
  --white: ${theme.foreground};
  --light-blue: ${theme.primary};
  --button-color: var(--white);
  --button-background-color: var(--amazonOrange);
  --button-click: var(--darkAmazonOrange);
  --link-color: var(--amazonOrange);
  --form-color: var(--white);
  --input-color: ${theme.background};
  --input-background-color: ${theme.card};
  --font-family: "Inter", "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "sans-serif";
  --body-background: ${theme.background};
  --component-width-desktop: 460px;
  --component-width-mobile: 100%;
  --color-primary: ${theme.primary};
  --color-primary-accent: ${theme.accentForeground};
  --color-primary-highlight: ${theme.primary};
  --color-background: ${theme.background};
  --color-secondary: ${theme.secondary};
  --color-secondary-accent: ${theme.card};
  --color-danger: ${theme.destructive};
  --color-error: ${theme.destructive};
  --color-accent-brown: ${theme.mutedForeground};
  --color-accent-blue: ${theme.border};
  --gradient-blaze: linear-gradient(270deg, ${theme.primary} 0%, ${theme.primary} 100%);
  --color-blue: ${theme.primary};
  --color-purple: ${theme.primary};
  --color-gray: ${theme.mutedForeground};
  --color-white: ${theme.foreground};
  --input-border: 1px solid ${theme.border};
  --input-padding: 0.5em 0.5em 0.3em 1em;
  --box-shadow: 1px 1px 4px 0 rgba(0,0,0,0.15);
  --button-height: 42px;
  --interactions-conversation-height: 250px;
  --ion-color-primary: ${theme.primary};
  --ion-color-primary-rgb: 114,92,249;
  --ion-color-primary-contrast: #fff;
  --ion-color-primary-contrast-rgb: 255,255,255;
  --ion-color-primary-shade: ${theme.background};
  --ion-color-primary-tint: ${theme.primary};
  --ion-color-secondary: ${theme.success};
  --ion-color-secondary-rgb: 34,197,94;
  --ion-color-secondary-contrast: #fff;
  --ion-color-secondary-contrast-rgb: 255,255,255;
  --ion-color-secondary-shade: ${theme.success};
  --ion-color-secondary-tint: ${theme.success};
  --ion-color-tertiary: ${theme.warning};
  --ion-color-tertiary-rgb: 245,158,11;
  --ion-color-tertiary-contrast: #fff;
  --ion-color-tertiary-contrast-rgb: 255,255,255;
  --ion-color-tertiary-shade: ${theme.warning};
  --ion-color-tertiary-tint: ${theme.warning};
  --ion-color-success: ${theme.success};
  --ion-color-success-rgb: 34,197,94;
  --ion-color-success-contrast: #fff;
  --ion-color-success-contrast-rgb: 255,255,255;
  --ion-color-success-shade: ${theme.success};
  --ion-color-success-tint: ${theme.success};
  --ion-color-warning: ${theme.warning};
  --ion-color-warning-rgb: 245,158,11;
  --ion-color-warning-contrast: #000;
  --ion-color-warning-contrast-rgb: 0,0,0;
  --ion-color-warning-shade: ${theme.warning};
  --ion-color-warning-tint: ${theme.warning};
  --ion-color-danger: ${theme.destructive};
  --ion-color-danger-rgb: 239,68,68;
  --ion-color-danger-contrast: #fff;
  --ion-color-danger-contrast-rgb: 255,255,255;
  --ion-color-danger-shade: ${theme.destructive};
  --ion-color-danger-tint: ${theme.destructive};
  --ion-color-light: ${theme.muted};
  --ion-color-light-rgb: 58,58,58;
  --ion-color-light-contrast: ${theme.foreground};
  --ion-color-light-contrast-rgb: 240,240,240;
  --ion-color-light-shade: ${theme.border};
  --ion-color-light-tint: ${theme.card};
  --ion-color-medium: ${theme.mutedForeground};
  --ion-color-medium-rgb: 160,160,160;
  --ion-color-medium-contrast: #000;
  --ion-color-medium-contrast-rgb: 0,0,0;
  --ion-color-medium-shade: ${theme.mutedForeground};
  --ion-color-medium-tint: ${theme.mutedForeground};
  --ion-color-dark: ${theme.background};
  --ion-color-dark-rgb: 31,31,31;
  --ion-color-dark-contrast: #fff;
  --ion-color-dark-contrast-rgb: 255,255,255;
  --ion-color-dark-shade: ${theme.background};
  --ion-color-dark-tint: ${theme.card};
}
`;

module.exports = {
  theme,
  antdTheme,
  cssRootVars,
};
